import { EventType } from "@/analytics/analytics-events";
import { MoveTimePointToWaypointProperties } from "@/analytics/data-organization-events-properties";
import { updateProject } from "@/components/common/project-provider/update-project";
import { ElementIconType } from "@/components/ui/icons/element-icon-type";
import { addIElementsToStore } from "@/store/ielements-store-utilities";
import { selectAreaFor } from "@/store/selections-selectors";
import { store } from "@/store/store";
import { setShowSpinner } from "@/store/ui/ui-slice";
import { isLocationMarker } from "@/types/ielement-extra-types";
import { selectIElement } from "@faro-lotv/app-component-toolbox";
import {
  Dropdown,
  DropdownOption,
  NoTranslate,
  TranslateVar,
  TruncatedFaroText,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  GUID,
  IElementRoomSection,
  IElementType,
  IElementTypeHint,
  isIElementAreaSection,
  isIElementRoomSection,
  isIElementTimeSeriesRoom,
  isIElementWithTypeAndHint,
} from "@faro-lotv/ielement-types";
import {
  selectAllIElementsOfType,
  selectAncestor,
} from "@faro-lotv/project-source";
import { createMutationMoveTimeTravelToRoom } from "@faro-lotv/service-wires";
import { Box } from "@mui/material";
import { groupBy } from "es-toolkit";
import { useMemo, useState, type JSX } from "react";
import { ContextMenuAction, ContextMenuActionType } from "../action-types";

// A unique key to represent the case when a waypoint has no area
const NO_AREA_KEY = "no-area-key";

export const MOVE_TIME_POINT_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.moveTimePoint,
  label: "Move to different waypoint",
  icon: ElementIconType.MoveElement,
  handler: async ({
    elementID,
    state,
    createDialog,
    setConfirmButtonDisabled,
    apiClients,
    dispatch,
    errorHandlers,
    openToast,
  }) => {
    const iElement = selectIElement(elementID)(state);
    if (!iElement) return;

    let targetWaypoint: IElementRoomSection | undefined;

    const currentWaypoint = selectAncestor(
      iElement,
      // We need to select the parent room section
      (el): el is IElementRoomSection =>
        isIElementRoomSection(el) && el.id !== elementID,
    )(state);

    const allAreas = selectAllIElementsOfType(isIElementAreaSection)(state);

    // Make sure that the "Rooms" group of an area is fetched, which is the direct child of an area.
    // Note: It may also not exist in areas without rooms.
    const areRoomsGroupsFetched = allAreas.every((area) =>
      (area.childrenIds ?? []).every(
        (childId) => !!selectIElement(childId)(state),
      ),
    );
    if (!areRoomsGroupsFetched) {
      dispatch(setShowSpinner(true));
      try {
        const children = await apiClients.projectApiClient.getAllIElements({
          parentIds: allAreas.map((area) => area.id),
        });
        addIElementsToStore(children, dispatch);
        // We need to update the state, after adding new elements to the store.
        // eslint-disable-next-line require-atomic-updates
        state = store.getState();
      } finally {
        dispatch(setShowSpinner(false));
      }
    }

    // Make sure that all rooms in the rooms groups are fetched
    const allRoomsGroups = selectAllIElementsOfType((el) =>
      isIElementWithTypeAndHint(el, IElementType.group, IElementTypeHint.rooms),
    )(state);
    const areRoomsFetched = allRoomsGroups.every((roomsGroup) =>
      (roomsGroup.childrenIds ?? []).every(
        (roomId) => !!selectIElement(roomId)(state),
      ),
    );
    if (!areRoomsFetched) {
      dispatch(setShowSpinner(true));
      try {
        const elements = (
          await Promise.all([
            apiClients.projectApiClient.getAllIElements({
              parentIds: allRoomsGroups.map((group) => group.id),
            }),
            apiClients.projectApiClient.getAllIElements({
              types: [IElementType.timeSeries],
              typeHints: [IElementTypeHint.room],
            }),
          ])
        ).flat();
        addIElementsToStore(elements, dispatch);
        // We need to update the state, after adding new elements to the store.
        // eslint-disable-next-line require-atomic-updates
        state = store.getState();
      } finally {
        dispatch(setShowSpinner(false));
      }
    }

    // Collect all waypoints grouped be the area they are in
    const allWaypointsInAllAreas = selectAllIElementsOfType(
      (el): el is IElementRoomSection => {
        const parent = selectIElement(el.parentId)(state);

        return !!(
          isIElementRoomSection(el) &&
          parent &&
          !isIElementTimeSeriesRoom(parent)
        );
      },
    )(state);
    const allWaypoints: WaypointsInArea[] = Object.entries(
      groupBy(
        allWaypointsInAllAreas,
        (el) => selectAreaFor(el)(state)?.id ?? NO_AREA_KEY,
      ),
    ).map(([areaId, waypoints]) => ({
      areaId: areaId === NO_AREA_KEY ? undefined : areaId,
      waypoints: waypoints.sort(compareWaypoints),
    }));

    if (!currentWaypoint) {
      openToast({ title: "The time point cannot be moved", variant: "error" });
      return;
    }

    await createDialog({
      title: (
        <>
          Move <TranslateVar name="elementName">{iElement.name}</TranslateVar>
        </>
      ),
      confirmText: "Move",
      isConfirmDisabled: true,
      content: (
        <MoveTimePointForm
          onTargetChanged={(value) => {
            targetWaypoint = value;
            setConfirmButtonDisabled(!value || value.id === currentWaypoint.id);
          }}
          currentWaypoint={currentWaypoint}
          allWaypoints={allWaypoints}
        />
      ),
      allowToCloseWithoutCancelling: true,
      size: "m",
      showXButton: true,
      onConfirm: async () => {
        if (!targetWaypoint) return false;

        Analytics.track<MoveTimePointToWaypointProperties>(
          EventType.moveTimePointToWaypoint,
          {
            isMovedToDifferentArea:
              selectAncestor(currentWaypoint, isIElementAreaSection)(state)
                ?.id !==
              selectAncestor(targetWaypoint, isIElementAreaSection)(state)?.id,
          },
        );

        try {
          await apiClients.projectApiClient.applyMutations([
            createMutationMoveTimeTravelToRoom(iElement.id, targetWaypoint.id),
          ]);

          // Fetch the changed sub-tree and update the local copy of the project
          await dispatch(
            updateProject({
              projectApi: apiClients.projectApiClient,
              iElementQuery: {
                ancestorIds: [currentWaypoint.id, targetWaypoint.id],
              },
            }),
          );

          openToast({
            title: (
              <>
                Time point moved to{" "}
                <TranslateVar name="targetArea">
                  {targetWaypoint.name}
                </TranslateVar>
              </>
            ),
          });

          return true;
        } catch (error) {
          errorHandlers.handleErrorWithDialog({
            title: "Unable to move Element",
            error,
          });
          return false;
        }
      },
    });
  },
};

/**
 * Compare function for sorting an array of rooms to match the capture-tree implementation.
 * It ensures that location markers are always at the end of the list.
 *
 * @param a Element A to compare
 * @param b Element B to compare
 * @returns The comparison result for the Array.sort function
 */
function compareWaypoints(
  a: IElementRoomSection,
  b: IElementRoomSection,
): number {
  const isALocationMarker = isLocationMarker(a, store.getState());
  const isBLocationMarker = isLocationMarker(b, store.getState());

  if (isALocationMarker && !isBLocationMarker) return 1;
  if (!isALocationMarker && isBLocationMarker) return -1;

  return 0;
}

type WaypointsInArea = {
  /** The area the waypoints belong to. Or undefined if the waypoints belong to no area */
  areaId?: GUID;

  /** The list of waypoints in the above area */
  waypoints: IElementRoomSection[];
};

type MoveDataSessionFormProps = {
  /** Callback executed when the target changed */
  onTargetChanged(iElement: IElementRoomSection | undefined): void;

  /** The waypoints of the iElement */
  currentWaypoint: IElementRoomSection;

  /** The list of all available waypoints the element can be moved into */
  allWaypoints: WaypointsInArea[];
};

function MoveTimePointForm({
  onTargetChanged,
  currentWaypoint,
  allWaypoints,
}: MoveDataSessionFormProps): JSX.Element {
  const [selectedWaypoint, setSelectedWaypoint] = useState<
    IElementRoomSection | undefined
  >(currentWaypoint);

  const options = useMemo(
    () =>
      allWaypoints
        .map(({ areaId, waypoints }): DropdownOption[] => [
          areaId
            ? {
                isHeader: true,
                key: areaId,
                value: areaId,
                label: (
                  <NoTranslate>
                    {selectIElement(areaId)(store.getState())?.name}
                  </NoTranslate>
                ),
              }
            : {
                isHeader: true,
                key: "NO_AREA_KEY",
                value: "NO_AREA_KEY",
                label: "Waypoints without area",
              },
          ...waypoints.map((waypoint) => ({
            label: <NoTranslate>{waypoint.name}</NoTranslate>,
            value: waypoint.id,
            key: waypoint.id,
          })),
        ])
        .flat(),
    [allWaypoints],
  );

  return (
    <>
      <Box component="div" sx={{ mb: 3 }}>
        <TruncatedFaroText variant="bodyM">
          Current waypoint:{" "}
          <b>
            <TranslateVar name="waypointName">
              {currentWaypoint.name}
            </TranslateVar>
          </b>
        </TruncatedFaroText>
      </Box>

      <Dropdown
        label="Move to waypoint"
        fullWidth
        value={selectedWaypoint?.id}
        options={options}
        onChange={(ev) => {
          const waypoint = allWaypoints
            .map(({ waypoints }) => waypoints)
            .flat()
            .find((waypoint) => waypoint.id === ev.target.value);
          setSelectedWaypoint(waypoint);
          onTargetChanged(waypoint);
        }}
      />
    </>
  );
}
